import React, { useState } from 'react';
import { X, Phone, MessageSquare, Mail } from 'lucide-react';
import VoiceInputButton from '../../VoiceInputButton';

interface Contact {
  primaryName: string;
  primaryEmail: string;
  primaryPhone: string;
  secondaryName?: string;
  secondaryEmail?: string;
  secondaryPhone?: string;
}

interface IntakeDocument {
  id: string;
  name: string;
  status: 'Completed' | 'Pending' | 'Not Started';
  completedDate?: string;
}

interface Inquiry {
  id: string;
  name: string;
  age: number;
  contact: Contact;
  billingType: 'Insurance' | 'Self-pay';
  status: 'Scheduled' | 'Documents completed' | 'No upcoming appointments' | 'Contact info missing';
  dateAdded: Date;
  hasAppointment?: boolean;
  documentsCompleted?: boolean;
  location?: string;
  clinician?: string;
  intakeProgress?: number;
  legalFirstName?: string;
  legalLastName?: string;
  middleName?: string;
  suffix?: string;
  preferredName?: string;
  isMinor?: boolean;
  dateOfBirth?: string;
  gender?: string;
  genderIdentity?: string;
  relationshipStatus?: string;
  employmentStatus?: string;
  race?: string[];
  raceDetails?: string;
  preferredLanguage?: string;
  clientNotes?: string;
  dateFirstSeen?: string;
  referredBy?: string;
  individualBillingType?: string;
  groupBillingType?: string;
  primaryInsurance?: string;
  insuranceMemberId?: string;
  insuranceStartDate?: string;
}

interface InquiryDetailPanelProps {
  isOpen: boolean;
  onClose: () => void;
  inquiry: Inquiry;
  onSave?: (updatedInquiry: Inquiry) => void;
}

const InquiryDetailPanel: React.FC<InquiryDetailPanelProps> = ({
  isOpen,
  onClose,
  inquiry,
  onSave
}) => {
  const [activeTab, setActiveTab] = useState<'notes' | 'activity'>('activity');
  const [formData, setFormData] = useState<Inquiry>(inquiry);
  const [isIntakeExpanded, setIsIntakeExpanded] = useState(false);

  // Intake documents (populated from real data when available)
  const intakeDocuments: IntakeDocument[] = [];

  const completedDocs = intakeDocuments.filter(d => d.status === 'Completed').length;
  const totalDocs = intakeDocuments.length;

  // Activity data (populated from real data when available)
  const activities: { id: string; type: string; description: string; timestamp: string; user?: string; icon: string }[] = [];

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      width: '500px',
      height: '100vh',
      backgroundColor: 'white',
      boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#2563eb' }}>
            {inquiry.name}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={24} color="#6b7280" />
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <span style={{
            padding: '4px 12px',
            backgroundColor: '#f3f4f6',
            borderRadius: '4px',
            fontSize: '13px',
            fontWeight: 500,
            color: '#374151'
          }}>
            Adult
          </span>
          <span style={{
            padding: '4px 12px',
            backgroundColor: '#dbeafe',
            borderRadius: '4px',
            fontSize: '13px',
            fontWeight: 500,
            color: '#2563eb'
          }}>
            Prospective
          </span>
          <span style={{ fontSize: '14px', color: '#d1d5db' }}>
            · Age {inquiry.age} ·
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Phone size={16} color="#6b7280" />
            <MessageSquare size={16} color="#6b7280" />
            <Mail size={16} color="#6b7280" />
          </div>
        </div>

        {/* Secondary Contact */}
        {inquiry.contact.secondaryName && (
          <div style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '6px',
            marginBottom: '16px'
          }}>
            <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '14px', color: '#1f2937' }}>
              {inquiry.contact.secondaryName} (Spouse)
            </div>
            <div style={{ display: 'flex', gap: '12px', fontSize: '13px', color: '#d1d5db' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <X size={14} color="#ef4444" />
                <Phone size={14} />
                <Mail size={14} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Client Preferences */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', margin: 0, color: '#1f2937' }}>
          Client preferences
        </h3>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px', color: '#1f2937' }}>
            Billing type
          </div>
          <div style={{ marginBottom: '12px', marginLeft: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ fontSize: '14px', fontWeight: 400, color: '#1f2937', minWidth: '160px' }}>
              Individual appointments
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer', color: '#1f2937' }}>
                <input
                  type="radio"
                  name="individualBilling"
                  checked={formData.billingType === 'Self-pay'}
                  readOnly
                />
                Self-pay
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer', color: '#1f2937' }}>
                <input
                  type="radio"
                  name="individualBilling"
                  checked={formData.billingType === 'Insurance'}
                  readOnly
                />
                Insurance
              </label>
            </div>
          </div>

          <div style={{ marginLeft: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ fontSize: '14px', fontWeight: 400, color: '#1f2937', minWidth: '160px' }}>
              Group appointments
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer', color: '#1f2937' }}>
                <input
                  type="radio"
                  name="groupBilling"
                  checked
                  readOnly
                />
                Self-pay
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer', color: '#1f2937' }}>
                <input
                  type="radio"
                  name="groupBilling"
                  readOnly
                />
                Insurance
              </label>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <label style={{ fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>
            Clinician
          </label>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#1f2937'
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: '#ef4444',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 600
            }}>
              TM
            </div>
            <span style={{ fontSize: '14px' }}>Tara Miller (You)</span>
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '15px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
            Location
          </label>
          <select
            value={formData.location || 'Video Office'}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: 'white',
              color: '#1f2937'
            }}
          >
            <option>🟢 Video Office</option>
            <option>Unassigned</option>
          </select>
        </div>
      </div>

      {/* Intake Progress */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
        <div
          onClick={() => setIsIntakeExpanded(!isIntakeExpanded)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            marginBottom: isIntakeExpanded ? '16px' : '0'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{ color: 'white', fontSize: '14px' }}>✓</span>
            </div>
            <span style={{ fontSize: '15px', fontWeight: 500, color: '#1f2937' }}>Intake documents</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              padding: '2px 8px',
              backgroundColor: '#d1fae5',
              color: '#065f46',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 600
            }}>
              {completedDocs}/{totalDocs} completed
            </span>
            <span style={{ fontSize: '18px', color: '#d1d5db' }}>
              {isIntakeExpanded ? '∧' : '∨'}
            </span>
          </div>
        </div>

        {isIntakeExpanded && (
          <div style={{ marginLeft: '28px' }}>
            {intakeDocuments.map((doc) => (
              <div key={doc.id} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '14px', color: '#374151' }}>{doc.name}</span>
                  <span style={{ fontSize: '13px', color: '#d1d5db' }}>{doc.completedDate}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '2px'
                }}>
                  <span style={{
                    fontSize: '12px',
                    color: '#10b981',
                    fontWeight: 600
                  }}>
                    Completed
                  </span>
                  {doc.id === '9' && (
                    <button
                      style={{
                        color: '#2563eb',
                        fontSize: '12px',
                        fontWeight: 500,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      View
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Appointment */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{ color: 'white', fontSize: '14px' }}>✓</span>
            </div>
            <span style={{ fontSize: '15px', fontWeight: 500, color: '#1f2937' }}>Appointment</span>
          </div>
          <span style={{
            padding: '2px 8px',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 600
          }}>
            Scheduled
          </span>
        </div>
      </div>

      {/* Notes & Activity Tabs */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e5e7eb',
          padding: '0 24px'
        }}>
          <button
            onClick={() => setActiveTab('notes')}
            style={{
              padding: '12px 16px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'notes' ? '2px solid #2563eb' : '2px solid transparent',
              color: activeTab === 'notes' ? '#2563eb' : '#6b7280',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500
            }}
          >
            Notes
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            style={{
              padding: '12px 16px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'activity' ? '2px solid #2563eb' : '2px solid transparent',
              color: activeTab === 'activity' ? '#2563eb' : '#6b7280',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500
            }}
          >
            Activity
          </button>
        </div>

        <div style={{ padding: '20px 24px', flex: 1 }}>
          {activeTab === 'notes' ? (
            <div>
              {formData.clientNotes ? (
                <div>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    marginBottom: '16px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '14px', color: '#374151', whiteSpace: 'pre-wrap' }}>
                      {formData.clientNotes}
                    </div>
                  </div>
                  <button
                    onClick={() => setFormData({ ...formData, clientNotes: '' })}
                    style={{
                      color: '#2563eb',
                      fontSize: '14px',
                      fontWeight: 500,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0
                    }}
                  >
                    Edit note
                  </button>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <label style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937' }}>Client Notes</label>
                    <VoiceInputButton onResult={text => setFormData(prev => ({ ...prev, clientNotes: (prev.clientNotes || '') + ' ' + text }))} />
                  </div>
                  <textarea
                    value={formData.clientNotes || ''}
                    onChange={(e) => setFormData({ ...formData, clientNotes: e.target.value })}
                    placeholder="Click to start note..."
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      color: '#1f2937'
                    }}
                  />
                  <div style={{ fontSize: '12px', color: '#d1d5db', marginTop: '8px', marginBottom: '12px' }}>
                    This note will appear on the client's overview page
                  </div>
                  {formData.clientNotes && formData.clientNotes !== inquiry.clientNotes && (
                    <button
                      onClick={() => {
                        if (onSave) {
                          onSave(formData);
                        }
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    >
                      Save note
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              {activities.length === 0 ? (
                <div style={{ fontSize: '14px', color: '#9ca3af', textAlign: 'center', padding: '24px 0' }}>
                  No activity yet.
                </div>
              ) : (
                <>
                  <button
                    style={{
                      color: '#2563eb',
                      fontSize: '14px',
                      fontWeight: 500,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    View all activities ({activities.length}) ▼
                  </button>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {activities.map((activity) => (
                      <div key={activity.id} style={{ display: 'flex', gap: '12px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: activity.icon === '●' ? '#1f2937' : '#ef4444',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: activity.icon === '●' ? '24px' : '11px',
                          fontWeight: 600,
                          flexShrink: 0
                        }}>
                          {activity.icon}
                        </div>
                        <div>
                          <div style={{ fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
                            {activity.description}
                          </div>
                          <div style={{ fontSize: '13px', color: '#d1d5db' }}>
                            {activity.timestamp}
                            {activity.user && `, ${activity.user}`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InquiryDetailPanel;
